import { eventPost } from '@/lib/events';
import { apiv2Get } from '@/lib/apiv2';
import { currentUpdaterRole } from '@/config/program';

// Declared locally, not imported from TaskService: services stay mono-entity.
export interface ReportingActor {
  user_id?: string;
  firstname?: string;
  lastname?: string;
  role?: string;
}

export interface EligibilityCheckInput {
  withingsUserId: string | number;
  carePeriodId: string | number;
  taskId?: string | number;
  mode?: 'submit' | 'preview';
}

export interface AlignmentCondition {
  icd10: string;
  display: string;
}

export interface AlignmentInput {
  withingsUserId: string | number;
  carePeriodId?: string | number; // omit for a lead with no care period (sent only if present)
  taskId?: string | number;
  mbi: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string; // YYYY-MM-DD
  gender: string; // FHIR enum: male | female | other | unknown (app-mapped)
  trackCode: string; // eckm | ckm | msk | bh
  isProviderReferral: boolean; // real boolean — WF12 checks === true
  // STRICT (2026-07-11): vetted {icd10, display} pairs ONLY — the app derives
  // them via buildAlignmentPacket; a bare name can no longer ride to WF12/CMS.
  conditions: AlignmentCondition[];
  // Signed attestation packet (Option C) — forwarded to WF12 Record Alignment Success.
  reviewedBy?: string | null;
  reviewedByRole?: string | null;
  attestationText?: string | null;
  attestationSignature?: string | null;
}

// A quarter is one $report-data with BP and weight together; WF37 refuses the old split kinds.
export type ReportKind = 'baseline' | 'quarterly' | 'final';

// Values the operator entered for observations the reads would leave missing — typed, or
// reviewed after WF37 proposed them from the Elation chart. WF37 validates them server-side,
// RECORDS them in `measure_state` (never `lab_result` — banned 2026-08-05), and only then submits.
export interface ManualObservationInput {
  slug: string;
  value: number;
  collected_at?: string;
  /**
   * Where the operator says the number came from. CLAIMED, never verified: the submit lane makes
   * no Elation read, so WF37 records this for the audit row and lets it change nothing.
   */
  source_claimed?: 'operator' | 'elation_lab_report';
  /** Parts, not a preformatted string — WF37 rebuilds the reference itself (bug class #13). */
  source_ref?: { report_id?: string; accession?: string; loinc?: string };
}

export interface ReportingSubmissionInput {
  withingsUserId: string | number;
  carePeriodId: string | number;
  taskId?: string | number;
  reportKind: ReportKind;
  mode?: 'submit' | 'preview';
  manualObservations?: ManualObservationInput[];
  actor?: ReportingActor;
  /**
   * Audited decision to file with a REQUIRED OAP measure missing. WF37 blocks such a
   * submission unless this carries a real typed justification (>= 20 chars) — filing
   * incomplete costs that beneficiary attainment on the missing measure, but missing the
   * 60-day baseline window unaligns them entirely, so the choice has to be available.
   */
  overrideIncomplete?: { reason: string; actorRole?: string } | null;
}

// App-driven CMS $align (cms_alignment_requested → WF5 → WF12). Async at apiv2
// (the CMS poll outlasts the 3s SYNC_TIMEOUT): this acks immediately and the
// verdict lands on the cms_submissions row (operation='align'), which the modal
// polls. is_provider_referral is a real boolean; conditions is an array of
// {icd10,display} objects; the attestation packet rides along for WF12 to persist.
export async function fireAlignment(
  {
    withingsUserId, carePeriodId, taskId, mbi, firstName, lastName,
    dateOfBirth, gender, trackCode, isProviderReferral, conditions,
    reviewedBy, reviewedByRole, attestationText, attestationSignature,
  }: AlignmentInput,
): Promise<unknown> {
  return eventPost('cms_alignment_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    task_id: taskId,
    mbi,
    first_name: firstName,
    last_name: lastName,
    date_of_birth: dateOfBirth,
    gender,
    track_code: trackCode,
    is_provider_referral: isProviderReferral,
    conditions,
    reviewed_by: reviewedBy ?? null,
    reviewed_by_role: reviewedByRole ?? null,
    attestation_text: attestationText ?? null,
    attestation_signature: attestationSignature ?? null,
  });
}

export async function triggerCmsUnalignment(
  withingsUserId: string | number,
  carePeriodId: string | number | undefined,
  reason: string,
  conditions: string[] = [],
): Promise<unknown> {
  return eventPost('unalignment_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    reason,
    conditions,
    occurred_at: new Date().toISOString(),
    trigger_source: 'backoffice',
  });
}

// mode='preview' returns the FHIR Parameters body for operator review; 'submit' runs the full CMS flow.
export async function runCmsEligibilityCheck(
  {
    withingsUserId, carePeriodId, taskId, mode = 'submit',
  }: EligibilityCheckInput,
): Promise<unknown> {
  return eventPost('cms_eligibility_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    task_id: taskId,
    mode,
  });
}

// reportKind selects the ACCESS report Bundle shape. preview vs submit same semantics as eligibility.
export async function runCmsReportingSubmission(
  {
    withingsUserId, carePeriodId, taskId, reportKind, mode = 'submit', manualObservations, actor,
    overrideIncomplete,
  }: ReportingSubmissionInput,
): Promise<unknown> {
  const manual = (manualObservations || []).filter((m) => m && Number.isFinite(m.value));
  // Identity rides the `actor` wrapper (bare sent_by_* are guarded) — WF37 stamps it as the
  // manual-entry provenance on the cms_submissions row.
  const identity = Object.fromEntries(Object.entries({
    user_id: actor?.user_id,
    firstname: actor?.firstname,
    lastname: actor?.lastname,
    role: actor?.role ?? currentUpdaterRole(),
  }).filter(([, v]) => v != null));
  return eventPost('cms_reporting_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    task_id: taskId,
    report_kind: reportKind,
    mode,
    ...(manual.length > 0 ? { manual_observations: manual, actor: identity } : {}),
    // Only sent when the operator actually typed a justification — an empty object would
    // read downstream as "an override was attempted", which is a different claim.
    ...(overrideIncomplete?.reason?.trim()
      ? {
        override_incomplete: {
          reason: overrideIncomplete.reason.trim(),
          actor_role: overrideIncomplete.actorRole ?? actor?.role ?? currentUpdaterRole(),
        },
        actor: identity,
      }
      : {}),
  });
}

export interface CmsSubmissionRow {
  correlation_id: string;
  withings_user_id: string;
  id_care_period: string | null; // apiv2's name on this entity; there is no care_period_id
  operation: string;
  status: string;
  result_code: string | null;
  result_display: string | null;
  mbi_last4: string | null;
  submitted_at: string | null;
  finished_at: string | null;
  created: string | null; // apiv2 creation ts (ISO) — the field apiv2 actually returns
  created_at: string | null; // contract-spec alias; absent on apiv2 reads
  // WF37 stamps report_kind here — see logic/cmsWindows.ts milestoneReportDates.
  request_payload?: { report_kind?: string | null; [k: string]: unknown } | null;
}

// Reads the CMS submission rows WF12/WF24 wrote to our DB (Tier-1 GET).
// Returns newest-first. `operation` filters to one lifecycle op (e.g. 'align');
// omit it to read every operation (the test harness shows the latest of any).
export async function getCmsSubmissions(
  withingsUserId: string,
  operation?: string,
): Promise<CmsSubmissionRow[]> {
  // apiv2 pages at 50 by default; logic/baseline.ts must see every accepted filing of a period.
  const rows = await apiv2Get('cms-submissions', {
    withings_user_id: String(withingsUserId),
    ...(operation ? { operation } : {}),
    limit: 200,
  });
  const list: CmsSubmissionRow[] = Array.isArray(rows) ? rows : [];
  return [...list].sort((a, b) => String(b.created ?? b.created_at ?? '').localeCompare(String(a.created ?? a.created_at ?? '')));
}
